/**
 * Membership ordering helpers. Each membership tier carries an optional
 * `membership_hierarchy` number on its `config` (lower = junior tier, higher =
 * senior). The API lists memberships alphabetically by code; these helpers let
 * the admin UI present them in tier order instead, consistently across the
 * Memberships page and the per-tier ComPlan matrices.
 */

type MembershipLike = { code: string; config?: Record<string, unknown> | null };

/** The configured hierarchy rank, or null when unset / non-numeric. */
export function membershipHierarchy(config: unknown): number | null {
  const h = (config as { membership_hierarchy?: unknown } | null | undefined)?.membership_hierarchy;
  return typeof h === 'number' ? h : null;
}

/**
 * Comparator that orders memberships ascending by hierarchy (junior → senior).
 * Tiers without a configured hierarchy sort last; ties (or both unset) fall back
 * to code A→Z so the order is always stable.
 */
export function compareMembershipHierarchy(a: MembershipLike, b: MembershipLike): number {
  const ha = membershipHierarchy(a.config);
  const hb = membershipHierarchy(b.config);
  if (ha !== hb) {
    if (ha === null) return 1;
    if (hb === null) return -1;
    return ha - hb;
  }
  return a.code.localeCompare(b.code);
}

/** Convenience: a hierarchy-sorted copy of the given memberships. */
export function sortMembershipsByHierarchy<T extends MembershipLike>(memberships: T[]): T[] {
  return [...memberships].sort(compareMembershipHierarchy);
}
